import { ExtranetOrderScheduleDeadlineDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetTable,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacH4,
  PharmacH6,
  PharmacIcon,
  PharmacSwitch,
  PharmacText,
  PharmacTooltip,
} from '@pharmac/ui'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useState } from 'react'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { OrderSchedulesTableRow } from '../../components/order-schedules/OrderSchedulesTableRow'
import { NoResultsResponse } from '../../components/responses/NoResultsResponse'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'

export const OrderSchedulesView = () => {
  const { minDesktop, maxMobile } = useBreakpoint()
  const { t } = useLanguage()
  const { customer } = useCustomerContext()
  const [scheduleDeadlines, setScheduleDeadlines] = useState(false)

  const addMonths = (date: Date, months: number) => {
    format(date.setMonth(date.getMonth() + months), 'yyyy-MM-dd')
    return date
  }
  const dateNow = format(new Date(), 'yyyy-MM-dd')
  const sixMonthsFromNow = format(addMonths(new Date(), 6), 'yyyy-MM-dd')

  const getOrderScheduleDeadlines = (
    customerId?: number
  ): UseQueryOptions<Array<ExtranetOrderScheduleDeadlineDto>> => ({
    queryFn: async () =>
      await api.orders.getOrderScheduleDeadlines(customerId as number, {
        startDate: dateNow,
        endDate: sixMonthsFromNow,
      }),
    queryKey: ['__getOrderScheduleDeadlines', customerId],
    enabled: typeof customerId === 'number',
  })
  const { data: orderScheduleDeadlines } = useQuery(getOrderScheduleDeadlines(customer?.id))

  const exceptionDeadlines = orderScheduleDeadlines?.filter((a) => a.hasExceptions)

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        paddingMediumBottom
        paddingSmallHorizontal={!minDesktop}
      >
        <ExtranetContainer
          full
          alignCenter
          paddingLargeVertical={minDesktop}
          paddingLargeTop={!minDesktop}
          paddingMediumBottom={!minDesktop}
        >
          <ExtranetContainer half>
            <PharmacH4
              style={{ color: 'var(--color-primary)' }}
              paddingMediumRight
            >
              {t('extranet:menu.omaAnnosOrderSchedules')}
            </PharmacH4>
            <PharmacTooltip
              maxWidth={maxMobile ? '295px' : '500px'}
              position="top-start"
              handle={
                <PharmacIcon
                  style={{ color: 'var(--color-info)' }}
                  size="24px"
                  icon="info"
                />
              }
            >
              <PharmacText>{t('extranet:orderSchedules.titleTooltip')}</PharmacText>
            </PharmacTooltip>
          </ExtranetContainer>

          {minDesktop && (
            <ExtranetContainer
              half
              alignCenter
              justifyEnd
            >
              <PharmacH6 paddingMediumRight>
                {t('extranet:orderSchedules.onlyExceptionsSwitch')}
              </PharmacH6>
              <PharmacSwitch
                marginSmallTop
                value={!scheduleDeadlines}
                onChange={() => setScheduleDeadlines(!scheduleDeadlines)}
              />
            </ExtranetContainer>
          )}
        </ExtranetContainer>
      </ExtranetContainer>

      {!minDesktop && (
        <ExtranetContainer
          full
          flexRow
          paddingSmallHorizontal
          paddingMediumBottom
          alignCenter
        >
          <PharmacH6 paddingMediumRight>
            {t('extranet:orderSchedules.onlyExceptionsSwitch')}
          </PharmacH6>
          <PharmacSwitch
            marginSmallTop
            value={!scheduleDeadlines}
            onChange={() => setScheduleDeadlines(!scheduleDeadlines)}
          />
        </ExtranetContainer>
      )}
      <ExtranetContainer
        full
        paddingSmallHorizontal={!minDesktop}
      >
        <ExtranetTable>
          {!scheduleDeadlines ? (
            <>
              {exceptionDeadlines?.map((a, i) => (
                <OrderSchedulesTableRow
                  key={i}
                  orderSchedules={a}
                />
              ))}
            </>
          ) : (
            <>
              {orderScheduleDeadlines?.map((a, i) => (
                <OrderSchedulesTableRow
                  key={i}
                  orderSchedules={a}
                />
              ))}
            </>
          )}
          {exceptionDeadlines?.length === 0 && !scheduleDeadlines && (
            <NoResultsResponse
              title={t('extranet:orderSchedules.noExceptionSchedules')}
              icon="smile"
              description={t('extranet:productShortages.allGood')}
            />
          )}
          {orderScheduleDeadlines?.length === 0 && scheduleDeadlines && (
            <NoResultsResponse description={t('extranet:orderSchedules.noOrderSchedulesDesc')} />
          )}
        </ExtranetTable>
      </ExtranetContainer>
    </ExtranetWrapper>
  )
}
